import { useEffect, useRef } from 'react';
import { useSharedFile } from '../contexts/SharedFileContext';
import { toast } from 'sonner';

/**
 * ShareIntentListener (send-intent@7 — de.mindlib.sendIntent)
 *
 * Architecture note:
 *   The send-intent v7 plugin registers a SEPARATE Android Activity called
 *   SendIntentActivity (declared in AndroidManifest.xml) that catches
 *   android.intent.action.SEND. When the user shares a file, Android launches
 *   SendIntentActivity, which boots a fresh WebView with the same web app.
 *
 *   So every share is effectively a "cold start" for the share-handling
 *   WebView. Calling checkSendIntentReceived() on mount is the canonical way
 *   to grab the file. The plugin has already copied it to the app's internal
 *   storage, returning a file:// URI we can read via @capacitor/filesystem.
 *
 *   After the file is captured, we store it in SharedFileContext so Layout.js
 *   can redirect to the right page. The host page is responsible for calling
 *   SendIntent.finish() after the upload is complete (to close
 *   SendIntentActivity and return control to the normal MainActivity).
 */
const ShareIntentListener = () => {
  const { setFile, setPendingIntent } = useSharedFile();
  const processingRef = useRef(false);

  const processIntent = async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    try {
      const { SendIntent } = await import('send-intent');

      let result;
      try {
        result = await SendIntent.checkSendIntentReceived();
      } catch (_) {
        // Plugin rejects with "No processing needed" when intent isn't SEND
        processingRef.current = false;
        return;
      }

      if (!result || !result.url) {
        processingRef.current = false;
        return;
      }

      setPendingIntent(true);

      try {
        const resultUrl = decodeURIComponent(result.url);
        const fileName = result.title || 'compartilhado';
        const fileType = result.type || 'application/octet-stream';

        // Read the copied file via @capacitor/filesystem (returns base64)
        const { Filesystem } = await import('@capacitor/filesystem');
        const { data } = await Filesystem.readFile({ path: resultUrl });

        // data is base64 string — convert to Blob → File
        const byteChars = atob(data);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: fileType });
        const file = new File([blob], fileName, { type: fileType });

        toast.success(`Arquivo recebido: ${fileName}`);
        setFile(file, fileName, fileType);
      } catch (e) {
        console.error('Error reading shared file:', e);
        toast.error(`Erro ao ler arquivo compartilhado: ${e.message}`);
        setPendingIntent(false);
      }
    } catch (error) {
      // send-intent not available (web environment) — silent
      console.log('SendIntent not available:', error?.message);
    }

    processingRef.current = false;
  };

  // Run once on mount. Every share opens a fresh SendIntentActivity, so this
  // mount point is where the file is always waiting.
  useEffect(() => {
    if (!window.Capacitor) return;

    setPendingIntent(true);
    processIntent();

    // Safety release: if nothing arrives within 1.2s, clear the hold.
    const t = setTimeout(() => setPendingIntent(false), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default ShareIntentListener;
